import { newId, slugify, type DocumentContent, type Section } from '@imsenta/shared';

/** Immutably updates the tree, returning a new content object. */
function mapTree(sections: Section[], fn: (section: Section) => Section | null): Section[] {
  const out: Section[] = [];
  for (const section of sections) {
    const mapped = fn(section);
    if (!mapped) continue;
    out.push({ ...mapped, children: mapTree(mapped.children, fn) });
  }
  return out;
}

export function updateSection(content: DocumentContent, id: string, patch: Partial<Section>): DocumentContent {
  return {
    ...content,
    sections: mapTree(content.sections, (section) => (section.id === id ? { ...section, ...patch } : section)),
  };
}

export function removeSection(content: DocumentContent, id: string): DocumentContent {
  return { ...content, sections: mapTree(content.sections, (section) => (section.id === id ? null : section)) };
}

export function newSection(title = 'New section'): Section {
  return {
    id: newId('sec'),
    title,
    anchor: slugify(title),
    numbered: true,
    body: { type: 'doc', content: [{ type: 'paragraph' }] },
    children: [],
  };
}

interface Located {
  parent: Section[] | null;
  siblings: Section[];
  index: number;
  section: Section;
  parentSection: Section | null;
}

function locate(sections: Section[], id: string, parentSection: Section | null = null): Located | null {
  for (let index = 0; index < sections.length; index += 1) {
    const section = sections[index]!;
    if (section.id === id) return { parent: parentSection ? parentSection.children : null, siblings: sections, index, section, parentSection };
    const found = locate(section.children, id, section);
    if (found) return found;
  }
  return null;
}

function clone(content: DocumentContent): DocumentContent {
  return JSON.parse(JSON.stringify(content)) as DocumentContent;
}

/** Inserts a sibling directly after `afterId`, or at the end when it is null. */
export function insertSection(content: DocumentContent, afterId: string | null, section: Section): DocumentContent {
  const next = clone(content);
  if (!afterId) {
    next.sections.push(section);
    return next;
  }
  const found = locate(next.sections, afterId);
  if (!found) {
    next.sections.push(section);
    return next;
  }
  found.siblings.splice(found.index + 1, 0, section);
  return next;
}

/** Adds a child section under `parentId`. */
export function insertChildSection(content: DocumentContent, parentId: string, section: Section): DocumentContent {
  const next = clone(content);
  const found = locate(next.sections, parentId);
  if (!found) return content;
  found.section.children.push(section);
  return next;
}

export function moveSection(content: DocumentContent, id: string, direction: -1 | 1): DocumentContent {
  const next = clone(content);
  const found = locate(next.sections, id);
  if (!found) return content;
  const target = found.index + direction;
  if (target < 0 || target >= found.siblings.length) return content;
  const [moved] = found.siblings.splice(found.index, 1);
  found.siblings.splice(target, 0, moved!);
  return next;
}

/** Demotes a section to be the last child of its previous sibling. */
export function indentSection(content: DocumentContent, id: string, maxDepth = 4): DocumentContent {
  const next = clone(content);
  const found = locate(next.sections, id);
  if (!found || found.index === 0) return content;
  const previous = found.siblings[found.index - 1]!;
  if (depthOf(next, previous.id) >= maxDepth) return content;
  const [moved] = found.siblings.splice(found.index, 1);
  previous.children.push(moved!);
  return next;
}

/** Promotes a section to sit after its parent. */
export function outdentSection(content: DocumentContent, id: string): DocumentContent {
  const next = clone(content);
  const found = locate(next.sections, id);
  if (!found?.parentSection) return content;
  const grandparent = locate(next.sections, found.parentSection.id);
  if (!grandparent) return content;
  const [moved] = found.siblings.splice(found.index, 1);
  grandparent.siblings.splice(grandparent.index + 1, 0, moved!);
  return next;
}

export function depthOf(content: DocumentContent, id: string): number {
  let depth = 0;
  const walk = (sections: Section[], current: number): boolean => {
    for (const section of sections) {
      if (section.id === id) {
        depth = current;
        return true;
      }
      if (walk(section.children, current + 1)) return true;
    }
    return false;
  };
  walk(content.sections, 1);
  return depth;
}

export function findSectionById(content: DocumentContent, id: string): Section | null {
  return locate(content.sections, id)?.section ?? null;
}

export function duplicateSection(content: DocumentContent, id: string): DocumentContent {
  const found = locate(content.sections, id);
  if (!found) return content;
  // A fresh anchor per node: leaving it blank made two duplicates collide, and
  // `normalizeContent` would then hand them the same slug.
  const reId = (section: Section): Section => ({
    ...JSON.parse(JSON.stringify(section)),
    id: newId('sec'),
    anchor: slugify(`${section.anchor || section.title} copy`),
    locked: false,
    children: section.children.map(reId),
  });
  const copy = reId(found.section);
  copy.title = `${found.section.title} (copy)`;
  copy.anchor = slugify(copy.title);
  return insertSection(content, id, copy);
}

/** True when every section in the tree has a non-empty title. */
export function validateContent(content: DocumentContent): string[] {
  const problems: string[] = [];
  const walk = (sections: Section[], path: string[]) => {
    for (const section of sections) {
      const where = [...path, section.title || '(untitled)'].join(' › ');
      if (!section.title.trim()) problems.push(`A section under ${path.join(' › ') || 'the document'} has no title`);
      if (section.title.length > 300) problems.push(`The title of "${where}" is too long`);
      walk(section.children, [...path, section.title]);
    }
  };
  walk(content.sections, []);
  return problems;
}
